import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { getPICReport } from "../../api/physicalInventoryCounts/index.ts";
import { esES } from "@mui/x-data-grid/locales";

interface PICReportModalProps {
  picId: number;
}

interface ReportItem {
  id: number;
  productCode: string;
  productDescription: string;
  primerConteo: number;
  segundoConteo: number;
  diferencia: number;
  tercerConteo: number;
  enInventario: number;
  desviacion: number;
  valor: number;
}

const PICReportModal: React.FC<PICReportModalProps> = ({ picId }) => {
  const [reportData, setReportData] = useState<{
    items: ReportItem[];
    totalValuationDifference: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const { openSnack } = useSnack();

  useEffect(() => {
    fetchReport();
  }, [picId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await getPICReport(picId);
      const items = response.data.items.map((item: any, index: number) => ({
        id: index,
        productCode: item.productCode,
        productDescription: item.productDescription || "",
        primerConteo: item.primerConteo,
        segundoConteo: item.segundoConteo,
        diferencia: item.diferencia,
        tercerConteo: item.tercerConteo,
        enInventario: item.enInventario,
        desviacion: item.desviacion,
        valor: item.valor,
      }));
      setReportData({
        items,
        totalValuationDifference: response.data.totalValuationDifference,
      });
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;

    const headers = [
      "Código",
      "Descripción",
      "Primer Conteo",
      "Segundo Conteo",
      "Diferencia",
      "Tercer Conteo",
      "En Inventario",
      "Desviación",
      "Valor",
    ];

    const rows = reportData.items.map((item) => [
      item.productCode,
      item.productDescription,
      item.primerConteo,
      item.segundoConteo,
      item.diferencia,
      item.tercerConteo,
      item.enInventario,
      item.desviacion,
      item.valor,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `pic_report_${picId}_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: "productCode",
      headerName: "Código",
      flex: 0.15,
    },
    {
      field: "productDescription",
      headerName: "Descripción",
      flex: 0.2,
    },
    {
      field: "primerConteo",
      headerName: "Primer Conteo",
      flex: 0.12,
      type: "number",
    },
    {
      field: "segundoConteo",
      headerName: "Segundo Conteo",
      flex: 0.12,
      type: "number",
    },
    {
      field: "diferencia",
      headerName: "Diferencia",
      flex: 0.12,
      type: "number",
    },
    {
      field: "tercerConteo",
      headerName: "Tercer Conteo",
      flex: 0.12,
      type: "number",
    },
    {
      field: "enInventario",
      headerName: "En Inventario",
      flex: 0.12,
      type: "number",
    },
    {
      field: "desviacion",
      headerName: "Desviación",
      flex: 0.12,
      type: "number",
    },
    {
      field: "valor",
      headerName: "Valor",
      flex: 0.12,
      type: "number",
      valueFormatter: (value: number) => {
        return new Intl.NumberFormat("es-MX", {
          style: "currency",
          currency: "MXN",
        }).format(value);
      },
    },
  ];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          onClick={handleDownloadCSV}
          disabled={!reportData}
        >
          Descargar CSV
        </Button>
      </GridToolbarContainer>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box p={3}>
        <Typography>No se pudo cargar el reporte</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, minHeight: 500 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Diferencia Total de Valuación
          </Typography>
          <Typography
            variant="h4"
            color={
              reportData.totalValuationDifference >= 0
                ? "success.main"
                : "error.main"
            }
          >
            {new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN",
            }).format(reportData.totalValuationDifference)}
          </Typography>
        </CardContent>
      </Card>
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={reportData.items}
          columns={columns}
          loading={loading}
          slots={{
            toolbar: CustomToolbar,
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PICReportModal;

